// The guest's localStorage, on every taste page (the layout's <body>).
//
//   * prunes expired guest data on every visit;
//   * on the library, sends a signed-in account whose browser still holds
//     guest notes through the transfer page (the legacy boot path did this
//     after /api/me succeeded);
//   * on the public event page, stores the event (the `event` target
//     carries the GuestEvent JSON), ticks the rows the browser holds a note
//     for, and closes the untasted rows at the three-note limit;
//   * on the sign-in prompt, fills in how many notes are waiting.
import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import {
  getGuestNotes, getGuestNoteCount, hasReachedGuestLimit, hasGuestNoteForEventWine,
  saveGuestEvent, pruneGuestData
} from "../guest/storage"

export default class extends Controller {
  static targets = ["event", "row", "limitPrompt", "noteCount", "noteWord"]
  static values = {
    signedIn: Boolean,
    transferUrl: String,
    redirect: Boolean
  }

  connect() {
    pruneGuestData()
    if (this.signedInValue && this.redirectValue && getGuestNotes().length > 0) {
      Turbo.visit(this.transferUrlValue)
      return
    }
    this.paintRows()
    this.paintCount()
  }

  eventTargetConnected(element) {
    try {
      saveGuestEvent(JSON.parse(element.dataset.event))
    } catch {
      // A page without a parsable event stores nothing.
    }
  }

  paintRows() {
    if (!this.hasRowTarget) return
    const atLimit = hasReachedGuestLimit()
    this.rowTargets.forEach((row) => {
      const tasted = hasGuestNoteForEventWine(row.dataset.eventWineId)
      const tastable = row.dataset.tastable === "true"
      const link = row.querySelector(".event-lineup-tap")
      if (!link) return
      link.classList.toggle("tasted", tasted)
      link.classList.toggle("untasted", !tasted)
      const disabled = !tastable || tasted || atLimit
      link.classList.toggle("disabled", disabled)
      if (disabled) {
        link.setAttribute("aria-disabled", "true")
        link.setAttribute("tabindex", "-1")
      } else {
        link.removeAttribute("aria-disabled")
        link.removeAttribute("tabindex")
      }
      const cta = link.querySelector(".event-lineup-cta")
      if (cta) {
        cta.textContent = tasted ? "Tasted" : "Taste ›"
        cta.classList.toggle("wk-toast", tasted)
        cta.classList.toggle("wk-tag", !tasted)
      }
      const state = link.querySelector(".event-lineup-state")
      if (state) state.textContent = tasted ? "tasted" : ""
    })
    if (this.hasLimitPromptTarget) this.limitPromptTarget.hidden = !atLimit
  }

  paintCount() {
    if (!this.hasNoteCountTarget) return
    const count = getGuestNoteCount()
    this.noteCountTarget.textContent = String(count)
    if (this.hasNoteWordTarget) this.noteWordTarget.textContent = count === 1 ? "note" : "notes"
  }
}
